import React, { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa6';
import { CSVFileEntry } from '../../../types/common';

interface FileMetadataTableProps {
  entries: CSVFileEntry[];
  colorTheme: 'blue' | 'green';
}

const themeClasses = {
  blue: {
    button: 'text-primary hover:text-primary/80 hover:bg-accent',
    headerBg: 'bg-accent/60',
    headerText: 'text-accent-foreground',
    rowHover: 'hover:bg-accent/30',
  },
  green: {
    button: 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50',
    headerBg: 'bg-emerald-50/60',
    headerText: 'text-emerald-900',
    rowHover: 'hover:bg-emerald-50/30',
  },
};

const formatNumber = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return '–';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  return parseFloat(num.toFixed(2)).toString();
};

const formatUTC = (utcString?: string): string => {
  if (!utcString) return '–';
  try {
    const d = new Date(utcString);
    if (isNaN(d.getTime())) return utcString;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  } catch {
    return utcString;
  }
};

const FileMetadataTable: React.FC<FileMetadataTableProps> = memo(({ entries, colorTheme }) => {
  const { t } = useTranslation('training');
  const [isOpen, setIsOpen] = useState(false);
  const theme = themeClasses[colorTheme];

  // Only show uploaded files that have metadata
  const uploadedEntries = entries.filter(e => e.storagePath);

  if (uploadedEntries.length === 0) return null;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-150 ${theme.button}`}
      >
        {isOpen ? <FaChevronUp className="text-[9px]" /> : <FaChevronDown className="text-[9px]" />}
        {t('csvFileUploader.fileMetadata.toggle')}
      </button>

      {isOpen && (
        <div className="mt-2 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-xs">
            <thead>
              <tr className={theme.headerBg}>
                <th className={`px-3 py-2 text-left font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.file')}</th>
                <th className={`px-3 py-2 text-left font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.utcMin')}</th>
                <th className={`px-3 py-2 text-left font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.utcMax')}</th>
                <th className={`px-3 py-2 text-right font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.zeitschrittweite')}</th>
                <th className={`px-3 py-2 text-right font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.offset')}</th>
                <th className={`px-3 py-2 text-right font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.datenpunkte')}</th>
                <th className={`px-3 py-2 text-right font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.numerischerAnteil')}</th>
                <th className={`px-3 py-2 text-right font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.valMin')}</th>
                <th className={`px-3 py-2 text-right font-semibold ${theme.headerText}`}>{t('csvFileUploader.table.valMax')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {uploadedEntries.map((entry) => (
                <tr key={entry.id} className={`${theme.rowHover} transition-colors`}>
                  <td className="px-3 py-2 font-medium text-foreground truncate max-w-[180px]" title={entry.fileName}>
                    {entry.fileName}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{formatUTC(entry.utcMin)}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{formatUTC(entry.utcMax)}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{formatNumber(entry.zeitschrittweite)}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{entry.offset || '–'}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{entry.datenpunkte ? Number(entry.datenpunkte).toLocaleString() : '–'}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{entry.numerischerAnteil ? `${entry.numerischerAnteil}%` : '–'}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{entry.min || '–'}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{entry.max || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

FileMetadataTable.displayName = 'FileMetadataTable';

export default FileMetadataTable;
